import { ChangeEvent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import '../ep.css';
import { showError } from '../api';
import { partnerApi } from './api';

/** Public page (no sign-in): an organization applies to list and manage its programs. */
export default function PartnerApply() {
  const history = useHistory();
  const [f, setF] = useState({ org_name: '', website: '', name: '', title: '', email: '', phone: '', message: '' });
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ id: number; name: string; host: string | null; managed: boolean }[]>([]);
  const [picked, setPicked] = useState<Map<number, string>>(new Map());
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [news, setNews] = useState(false);
  const [fromInvite, setFromInvite] = useState(0);
  const loc = useLocation();

  // Personal invite link: #/partners/apply?programs=PRG-0057,PRG-0058&org=Auburn%20University
  useEffect(() => {
    const qs = new URLSearchParams(loc.search);
    const codes = qs.get('programs');
    const org = qs.get('org');
    if (org) setF((v) => (v.org_name ? v : { ...v, org_name: org.slice(0, 200) }));
    if (!codes) return;
    partnerApi
      .listingsByCodes(codes)
      .then((rows) => {
        const m = new Map<number, string>();
        rows.filter((r) => !r.managed).forEach((r) => m.set(r.id, r.name));
        setPicked(m);
        setFound(rows);
        setFromInvite(m.size);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const search = async (v: string) => {
    setQ(v);
    if (v.trim().length >= 3) setFound(await partnerApi.listings(v.trim()).catch(() => []));
    else setFound([]);
  };
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const ready = f.org_name.trim() && f.name.trim() && emailOk;

  const submit = async () => {
    setBusy(true);
    try {
      const website = f.website.trim() && !/^https?:\/\//i.test(f.website.trim()) ? `https://${f.website.trim()}` : f.website.trim();
      const r = await partnerApi.apply({ ...f, website: website || undefined, program_ids: [...picked.keys()], news_opt_in: news });
      setDone(r.message || 'Thanks. GradMap reviews new partners within 2 business days.');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ep" style={{ padding: 0 }}>
      <header className="ep-pheader">
        <span className="ep-logo">gradmap</span>
        <span className="muted" style={{ fontSize: 17 }}>For program providers</span>
        <button type="button" className="btn sm" onClick={() => history.push('/public/login')}>Partner sign in</button>
      </header>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 64px' }}>
        {done ? (
          <section className="card" style={{ textAlign: 'center' }}>
            <h1>Application sent</h1>
            <p className="body" style={{ marginTop: 10, lineHeight: 1.55 }}>{done}</p>
            <p className="body" style={{ marginTop: 10, lineHeight: 1.55 }}>When you are approved, we email {f.email} with a link to set your password. GradMap never emails a password.</p>
          </section>
        ) : (
          <>
            <h1>List your programs on GradMap</h1>
            <p className="body" style={{ marginTop: 6, lineHeight: 1.55 }}>
              Students, parents and counselors use GradMap to find summer and year-round programs and track their applications. Approved partners keep their
              listings current: dates, prices, deadlines and what the application asks for. It is free.
            </p>
            <section className="card" style={{ marginTop: 16 }}>
              <h2>Your organization</h2>
              <div className="ep-grid2" style={{ marginTop: 12 }}>
                <div><label className="lbl" htmlFor="pa-org">Organization name</label><input id="pa-org" className="in" value={f.org_name} maxLength={200} onChange={set('org_name')} /></div>
                <div><label className="lbl" htmlFor="pa-web">Website</label><input id="pa-web" className="in" placeholder="yourprogram.org" value={f.website} onChange={set('website')} /></div>
                <div><label className="lbl" htmlFor="pa-name">Your name</label><input id="pa-name" className="in" value={f.name} maxLength={120} onChange={set('name')} /></div>
                <div><label className="lbl" htmlFor="pa-title">Your title (optional)</label><input id="pa-title" className="in" value={f.title} maxLength={120} onChange={set('title')} /></div>
                <div>
                  <label className="lbl" htmlFor="pa-email">Work email</label>
                  <input id="pa-email" className="in" type="email" value={f.email} onChange={set('email')} />
                  <p className="muted small" style={{ marginTop: 4 }}>Use your email at the organization's website, so we can confirm it is yours.</p>
                </div>
                <div><label className="lbl" htmlFor="pa-phone">Phone (optional)</label><input id="pa-phone" className="in" type="tel" value={f.phone} onChange={set('phone')} /></div>
              </div>
            </section>
            <section className="card">
              <h2>Which listings are yours?</h2>
              <p className="muted small" style={{ marginTop: 4 }}>
                {fromInvite
                  ? `We found ${fromInvite === 1 ? 'your program' : `your ${fromInvite} programs`} and picked ${fromInvite === 1 ? 'it' : 'them'} for you. Search to add more, or remove any that are not yours.`
                  : 'GradMap may already list your programs. Pick them and they move to your account once you are approved.'}
              </p>
              <input className="in" style={{ marginTop: 12 }} placeholder="Search by program or organization name" value={q} onChange={(e) => search(e.target.value)} aria-label="Search listings" />
              {found.map((x) => (
                <label key={x.id} className="check" style={{ padding: '8px 0', borderBottom: '1px solid var(--ep-line)' }}>
                  <input type="checkbox" disabled={x.managed} checked={picked.has(x.id)} onChange={(e) => { const m = new Map(picked); if (e.target.checked) m.set(x.id, x.name); else m.delete(x.id); setPicked(m); }} />
                  <span>{x.name}<span className="muted small">{`${x.host ? ` · ${x.host}` : ''}${x.managed ? ' · already managed' : ''}`}</span></span>
                </label>
              ))}
              {picked.size > 0 && (
                <div className="row" style={{ marginTop: 10 }}>
                  {[...picked.entries()].map(([id, n]) => (
                    <button key={id} type="button" className="chipb on" onClick={() => { const m = new Map(picked); m.delete(id); setPicked(m); }} aria-label={`Remove ${n}`}>{n} ×</button>
                  ))}
                </div>
              )}
            </section>
            <section className="card">
              <label className="lbl" htmlFor="pa-msg">Anything else (optional)</label>
              <textarea id="pa-msg" className="in" maxLength={2000} value={f.message} onChange={set('message')} />
              <label className="check" style={{ marginTop: 14 }}>
                <input type="checkbox" checked={news} onChange={(e) => setNews(e.target.checked)} />
                <span>Send me GradMap partner news by email: new features and reminders before families start searching. You can unsubscribe anytime.</span>
              </label>
            </section>
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn primary" disabled={!ready || busy} onClick={submit}>Send application</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
